import React, { useState, useEffect } from "react";
import "./LeaveHistory.css";
import apiClient from "../utils/apiClient"; // ✅ global API handler
import SessionHandler from "../components/SessionHandler"; // ✅ global session expiry modal

const LeaveHistory = () => {
  const [requests, setRequests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [monthFilter, setMonthFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    const fetchLeaveRequests = async () => {
      try {
        const data = await apiClient("/leaves/");
        if (!data) return; // Session expired handled by SessionHandler

        const leaveList = Array.isArray(data.results)
          ? data.results
          : Array.isArray(data)
          ? data
          : [];

        setRequests(leaveList);
        setFiltered(leaveList);
      } catch (error) {
        console.error("Error fetching leave requests:", error);
      }
    };

    fetchLeaveRequests();
  }, []);

  useEffect(() => {
    let filteredData = [...requests];

    if (monthFilter) {
      filteredData = filteredData.filter((req) => {
        const date = new Date(req.start_date);
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        return month === monthFilter;
      });
    }

    if (nameFilter.trim()) {
      filteredData = filteredData.filter((req) =>
        req.employee_name?.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    if (departmentFilter.trim()) {
      filteredData = filteredData.filter((req) =>
        req.employee_department
          ?.toLowerCase()
          .includes(departmentFilter.toLowerCase())
      );
    }

    if (statusFilter.trim()) {
      filteredData = filteredData.filter((req) => req.status === statusFilter);
    }

    setFiltered(filteredData);
  }, [monthFilter, nameFilter, departmentFilter, statusFilter, requests]);

  // Count tiles
  const totalCount = requests.length;
  const pendingCount = requests.filter((req) => req.status === "Pending").length;
  const approvedCount = requests.filter((req) => req.status === "Approved").length;
  const rejectedCount = requests.filter((req) => req.status === "Rejected").length;

  // CSV Download
  const handleDownloadCSV = () => {
    const headers = [
      "Employee Name",
      "Department",
      "Subject",
      "Start Date",
      "End Date",
      "Leave Type",
      "Days",
      "Status",
    ];

    const rows = filtered.map((req) => [
      req.employee_name,
      req.employee_department,
      req.subject,
      req.start_date,
      req.end_date,
      req.leave_type,
      req.days,
      req.status,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((value) => `"${value}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "leave_history.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="leave-dashboard">
      <h2>Leave History</h2>

      {/* Summary Tiles */}
      <div className="request-tiles11">
        <div className="tile11 total">Total: {totalCount}</div>
        <div className="tile11 pending">Pending: {pendingCount}</div>
        <div className="tile11 approved">Approved: {approvedCount}</div>
        <div className="tile11 rejected">Rejected: {rejectedCount}</div>
      </div>

      {/* Filters */}
      <div className="filters-row1">
        <input
          type="text"
          placeholder="Filter by Employee Name"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
        />
        <input
          type="text"
          placeholder="Filter by Department"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
        <select
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
        >
          <option value="">All Months</option>
          {[...Array(12)].map((_, i) => {
            const monthNum = (i + 1).toString().padStart(2, "0");
            return (
              <option key={monthNum} value={monthNum}>
                {new Date(0, i).toLocaleString("default", { month: "long" })}
              </option>
            );
          })}
        </select>
        <button className="btn2-download" onClick={handleDownloadCSV}>
          Download CSV
        </button>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="leave-table2">
          <thead>
            <tr>
              <th>Employee Name</th>
              <th>Department</th>
              <th>Subject</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Leave Type</th>
              <th>Days</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((req) => (
              <tr key={req.id}>
                <td>{req.employee_name}</td>
                <td>{req.employee_department}</td>
                <td>{req.subject}</td>
                <td>{req.start_date}</td>
                <td>{req.end_date}</td>
                <td>{req.leave_type}</td>
                <td>{req.days}</td>
                <td className={`status-${req.status?.toLowerCase()}`}>
                  {req.status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ✅ Global Session Expiry Modal */}
      <SessionHandler />
    </div>
  );
};

export default LeaveHistory;
